import { useForm } from '@mantine/form';

import { useCreateRole } from '../api/createRole';
import { useUpdateRole } from '../api/updateRole';
import { roleTypes } from '../types';

type Options = {
  role: roleTypes.Role | undefined;
  onSuccess: () => void;
  onValidationError?: () => void;
};

export const useRoleForm = ({ role, onSuccess, onValidationError }: Options) => {
  const form = useForm({
    initialValues: {
      name: role?.name ?? '',
      description: role?.description ?? '',
      permissions: role?.permissions ?? [],
      pageAccess: role?.pageAccess ?? {},
      managerDepartmentAccess: role?.managerDepartmentAccess ?? [],
    },
    validate: {
      name: (value) => {
        if (!value.trim()) return 'Name is required';
        if (value.trim().length > 100) return 'Name must be 100 characters or fewer';
        return null;
      },
    },
  });

  const createRole = useCreateRole({ config: { onSuccess } });
  const updateRole = useUpdateRole({ config: { onSuccess } });
  const isLoading = createRole.isLoading || updateRole.isLoading;

  const handleSubmit = form.onSubmit(
    ({
      name,
      description,
      permissions: selectedPermissions,
      pageAccess,
      managerDepartmentAccess,
    }) => {
      // Wildcard permission grants full access — pageAccess and managerDepartmentAccess
      // are irrelevant in that case and must be explicitly cleared in the DB so they
      // cannot resurface if wildcard is later removed.
      const isWildcard = selectedPermissions.includes('*');

      if (role) {
        updateRole.mutate({
          id: role._id,
          data: {
            name: name.trim(),
            // For edit: send the trimmed value even if empty so the user can clear
            // a previously set description. Server treats '' as "no description".
            ...(form.isDirty('description') && { description: description.trim() }),
            permissions: selectedPermissions,
            ...(isWildcard
              ? { pageAccess: {}, managerDepartmentAccess: [] }
              : {
                  ...(form.isDirty('pageAccess') && { pageAccess }),
                  ...(form.isDirty('managerDepartmentAccess') && { managerDepartmentAccess }),
                }),
          },
        });
      } else {
        createRole.mutate({
          name: name.trim(),
          // For create: omit empty description entirely so the field is never stored.
          description: description.trim() || undefined,
          permissions: selectedPermissions,
          ...(!isWildcard && form.isDirty('pageAccess') && { pageAccess }),
          ...(!isWildcard &&
            form.isDirty('managerDepartmentAccess') && { managerDepartmentAccess }),
        });
      }
    },
    () => onValidationError?.()
  );

  const togglePermission = (key: string) => {
    const current = form.values.permissions;
    const next = current.includes(key)
      ? current.filter((permission) => permission !== key)
      : [...current, key];
    form.setFieldValue('permissions', next);
  };

  return { form, isLoading, handleSubmit, togglePermission };
};
